import { CardSubWrapper, CardWrapper } from 'admin/components/wrappers';
import CustomDataGrid from 'app/components/CustomDataGrid/Index';
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';
import ClassCell from 'app/components/CustomDataGrid/Cells/ClassCell';
import EditButtonCell from 'app/components/CustomDataGrid/Cells/EditButtonCell';
import { SponsorWrapper } from 'services/openapi';
import { GridSortChangeEvent } from '@progress/kendo-react-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { usePermission } from 'hooks/usePermission';
import DeleteIconCell from 'app/components/CustomDataGrid/Cells/DeleteIconCell';
import { CustomDeletePopup } from 'app/components/CustomDeletePopup';
import { useNavigateToRoute } from 'utils/common';
import SponserNameCell from 'app/components/CustomDataGrid/Cells/SponserNameCell';

type Props = {
  setPageSkip: React.Dispatch<React.SetStateAction<number | undefined>>;
  pageSkip: number | undefined;
  sponsersDataList: SponsorWrapper | undefined;
  sortField: string | undefined;
  sortOrder: number | undefined;
  setSortField: React.Dispatch<React.SetStateAction<string>>;
  setSortOrder: React.Dispatch<React.SetStateAction<number>>;
  debounceRiderSearch: string;
  pageSize: number;
  fetchSponsersList: (
    searchInput?: string,
    sortBy?: string,
    sortOrder?: number,
    pageNumber?: number,
    pageSize?: number,
  ) => void;
  deleteSponsor: (
    sponsorId?: string,
    searchInput?: string,
    sortBy?: string,
    sortOrder?: number,
    pageNumber?: number,
    pageSize?: number,
  ) => void;
  sort: SortDescriptor[];
  setSort: Dispatch<SetStateAction<SortDescriptor[]>>;
};

const SponsersTable = ({
  setPageSkip,
  pageSkip,
  sponsersDataList,
  sortField,
  sortOrder,
  setSortField,
  setSortOrder,
  fetchSponsersList,
  debounceRiderSearch,
  deleteSponsor,
  sort,
  setSort,
  pageSize,
}: Props) => {
  const redirect = useNavigateToRoute();
  const { hasViewPermission } = usePermission();
  const [showDeletePopup, setShowDeletePopup] = React.useState<boolean>(false);
  const [getRiderId, setGetRiderId] = useState('');

  const initialSort = useMemo<SortDescriptor[]>(() => [], []);

  useEffect(() => {
    setSort(initialSort);
  }, [pageSize, initialSort, setSort]);
  const handleEditClick = e => {
    redirect(`/admin/sponsors/edit/${e.dataItem.sponsorId}`);
  };
  const handleDeleteRider = e => {
    setGetRiderId(e.dataItem.sponsorId);
    setShowDeletePopup(true);
  };

  const SponsersColumn = [
    {
      field: 'class',
      title: 'Class',
      cell: e => <ClassCell e={e} />,
      width: '300',
      sortable: false,
    },
    {
      field: 'name',
      title: 'Sponsors',
      cell: e => <SponserNameCell e={e} />,
      width: '300',
      sortable: true,
    },
    ...(hasViewPermission('0800101003')
      ? [
        {
          field: '',
          title: '',
          cell: e => (
            <EditButtonCell e={e} onClick={() => handleEditClick(e)} />
          ),
          width: '150',
          sortable: false,
        },
      ]
      : []),
    {
      field: 'action',
      title: 'Action',
      cell: e => (
        <DeleteIconCell e={e} deleteClick={() => handleDeleteRider(e)} />
      ),
      width: '80',
      sortable: false,
    },
  ];
  const handlePageChange = event => {
    setPageSkip(event.page.skip);
    fetchSponsersList(
      debounceRiderSearch,
      sortField,
      sortOrder,
      event.page.skip,
      pageSize,
    );
  };

  const handleSortChange = e => {
    let sortOrderValue = e.dir === 'asc' ? 0 : 1;
    setSortOrder(sortOrderValue);
    setSortField(e.field);
  };

  const handleDeleteRiderList = () => {
    const currentItemCount = sponsersDataList?.sponsors?.length ?? 0;

    // Calculate next page skip if we're deleting the last item on this page
    let nextPageSkip = pageSkip;
    if (currentItemCount <= 1 && pageSkip && pageSkip > 0) {
      nextPageSkip = pageSkip - pageSize;
      setPageSkip(nextPageSkip);
    }

    setShowDeletePopup(false);
    deleteSponsor(
      getRiderId,
      debounceRiderSearch,
      sortField,
      sortOrder,
      nextPageSkip ?? 0,
      pageSize,
    );
  };

  return (
    <>
      <CardWrapper style={{ paddingTop: '15px' }}>
        <CardSubWrapper className="d-flex"></CardSubWrapper>
        <GridWrapper>
          <CustomDataGrid
            showPagination
            data={sponsersDataList?.sponsors ?? []}
            columns={SponsersColumn}
            pageSize={pageSize}
            totalCount={sponsersDataList?.pageDetails?.count}
            onPageChange={event => {
              handlePageChange(event);
            }}
            skip={pageSkip}
            sortable={{
              allowUnsort: false,
              mode: 'single',
            }}
            sort={sort}
            onSortChange={(e: GridSortChangeEvent) => {
              let sortvalue = e.sort[0];
              setSort(e.sort);
              handleSortChange(sortvalue);
            }}
          />
          <CustomDeletePopup
            deleteIcon={true}
            headerUnderline={false}
            popupSubLabel="Are you sure you want to delete this sponsor?"
            className="body-scroll-lock height-full delete-update"
            popupLabel="Delete Sponsor"
            setVisible={setShowDeletePopup}
            visible={showDeletePopup}
            align="centre"
            btnLabel1="Cancel"
            btnLabel2="Delete"
            handlebtn2Click={handleDeleteRiderList}
            handlebtn1Click={() => {
              setShowDeletePopup(false);
            }}
          />
        </GridWrapper>
      </CardWrapper>
    </>
  );
};

export default SponsersTable;

export const GridWrapper = styled.div`
  .k-grid-content tr {
    &:hover {
      td {
        background: #f0f0f0 !important;
      }
    }
  }
  .k-grid-content tr td {
    cursor: pointer;
    .label {
      display: none;
    }
    input {
      font-family: 'Inter';
      font-weight: 500;
      font-size: 12px;
      line-height: 14.52px;
      max-width: 100%;
    }
    div {
      font-family: 'Inter';
      font-weight: 500;
      font-size: 12px;
      line-height: 14.52px;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }
    .custom-cell-style {
      width: fit-content;
      max-width: 100%;
      cursor: pointer;
    }
  }
  .k-grid-content tr td.team-cell {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
  }
  input:disabled {
    opacity: 1;
    cursor: pointer;
    padding: 0px !important;
    border-width: 0px;
    border-color: transparent !important;
    background-color: transparent !important;
    max-width: unset;
    width: fit-content;
  }
`;
